import React from 'react'
import { css, cx } from '@emotion/css'
import { theme as deprecatedTheme } from '../../styled/theme'
import { theme } from '@contrast-security-inc/design-system-foundations'
import {
  drilldownHeaderCss,
  metadataCss
} from '../../legacy-design-system/text'
import { PresenceIndicator } from '../presence-indicator'
import { ViolationIndicator } from '../applications-grid/violation-indicator'
import { AttackStatus } from '../attack-status'
import { OpenVulnerabilitiesSeverityThermometer } from '../thermometer'
import { AgentLanguage } from '../agent-language/agent-language'
import { getApplicationFlowMapRoute } from '../route'
import { ApplicationGrade } from '../application-grade'
import { AgentFeature } from '../agent-features'
import { OverflowDetector } from '../../legacy-design-system/overflow-detector'
import { WithTooltip } from '../../design-system'
import { PrimaryApplicationIndicator } from '../primary-application'
import { FormattedMessage } from 'react-intl'
import { Role } from '@contrast-security-inc/base-types'
import {
  CompliancePolicy,
  VulnerabilitySeverityBreakdown
} from '../applications-grid/shared'
import { ApplicationAttackStatus } from '../application-attack-status'
import { Placement } from '@popperjs/core'
import { Link } from '@contrast-security-inc/design-system-typography'

export const containerWidth = 340
export const containerHeight = 120

export interface ApplicationPreviewCardProps {
  readonly organizationID: string
  readonly applicationID: string
  readonly applicationName: string
  readonly url: string
  readonly lastActivity: string
  readonly online: boolean
  readonly primary: boolean
  readonly assess: boolean
  readonly hasAProtectedServer: boolean
  readonly isFreemiumOrganization: boolean
  readonly moduleCount: number
  readonly features?: AgentFeature[]
  readonly language: AgentLanguage
  readonly violatedCompliancePolicies: CompliancePolicy[]
  readonly organizationRole: Role
  readonly vulnerabilityBreakdown: VulnerabilitySeverityBreakdown
  readonly attackStatus: AttackStatus
  /**
   * Override the placement of all tooltips rendered in the preview card
   */
  readonly tooltipPlacement?: Placement
}

interface ApplicationPreviewCardState {
  readonly appNameOverflowing: boolean
  readonly urlOverflowing: boolean
}

export class ApplicationPreviewCard extends React.Component<
  ApplicationPreviewCardProps,
  ApplicationPreviewCardState
> {
  public constructor(props) {
    super(props)
    this.state = {
      appNameOverflowing: false,
      urlOverflowing: false
    }
  }

  public render() {
    const {
      applicationID,
      applicationName,
      organizationID,
      features,
      language,
      url,
      lastActivity,
      online,
      isFreemiumOrganization,
      primary,
      moduleCount,
      violatedCompliancePolicies,
      organizationRole,
      vulnerabilityBreakdown,
      assess,
      attackStatus,
      hasAProtectedServer,
      tooltipPlacement
    } = this.props

    const smallTextSize = 11
    const appNameMaxWidth = 130
    const appURLMaxWidth = 160

    const smallMetadataCss = cx(
      metadataCss,
      css`
        font-size: ${smallTextSize}px;
      `
    )
    const overflowCss = css`
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    `

    const IconSeparator = () => (
      <div
        className={css`
          width: 4px;
        `}
      />
    )

    const hasViolation =
      violatedCompliancePolicies.length > 0 && !isFreemiumOrganization

    return (
      <div
        data-e2e="connected-app-card"
        className={css`
          width: ${containerWidth}px;
          height: ${containerHeight}px;
          max-height: ${containerHeight}px;
          background-color: ${theme.color.white};
          border-color: ${theme.color.neutral50};
          border-width: 1.2px;
          border-radius: 10px;
          border-style: solid;
          padding: 8px 12px 8px 10px;
        `}
      >
        <div
          className={css`
            display: flex;
            flex-direction: row;
            align-items: center;
            margin-bottom: 16px;
          `}
        >
          <ApplicationGrade
            id={applicationID}
            organizationID={organizationID}
            language={language}
            features={features}
            tooltipPlacement={tooltipPlacement}
          />
          <IconSeparator />
          <IconSeparator />
          <div
            className={css`
              display: flex;
              flex-direction: column;
              justify-content: center;
            `}
          >
            <div
              className={css`
                display: flex;
                align-items: center;
                flex-direction: row;
              `}
            >
              <OverflowDetector>
                {({ doesOverflowParent, registerElement }) => {
                  const AppName = () => (
                    <span
                      ref={registerElement}
                      className={cx(
                        drilldownHeaderCss,
                        overflowCss,
                        css`
                          max-width: ${appNameMaxWidth}px;
                        `
                      )}
                      onMouseEnter={() => {
                        this.updateAppNameOverflowing(doesOverflowParent())
                      }}
                    >
                      {applicationName}
                    </span>
                  )

                  return doesOverflowParent() ? (
                    <WithTooltip
                      placement={'bottom'}
                      renderTooltipContent={() => applicationName}
                      children={() => <AppName />}
                    />
                  ) : (
                    <AppName />
                  )
                }}
              </OverflowDetector>
              <IconSeparator />
              <PresenceIndicator
                online={online}
                lastActivity={Number.parseInt(lastActivity, 10)}
                tooltipPlacement={tooltipPlacement}
              />
              {primary && !isFreemiumOrganization && (
                <React.Fragment>
                  <IconSeparator />
                  <PrimaryApplicationIndicator moduleCount={moduleCount} />
                </React.Fragment>
              )}
              {hasViolation && (
                <React.Fragment>
                  <IconSeparator />
                  <ViolationIndicator
                    organizationID={organizationID}
                    organizationRole={organizationRole}
                    violatedCompliancePolicies={violatedCompliancePolicies}
                    tooltipPlacement={tooltipPlacement}
                  />
                </React.Fragment>
              )}
            </div>

            <div
              className={cx(
                smallMetadataCss,
                css`
                  margin-top: -2px;
                `
              )}
            >
              <span
                className={css`
                  font-weight: ${deprecatedTheme.fontWeights.primary.bold};
                  margin-right: 4px;
                `}
              >
                {language.getDisplay()}
              </span>

              {assess && <FormattedMessage id={'title_featureTypeAssess'} />}
              {assess && hasAProtectedServer && <span>/</span>}
              {hasAProtectedServer && (
                <FormattedMessage id={'title_featureTypeProtect'} />
              )}
            </div>
          </div>
          <IconSeparator />
          <div
            className={css`
              display: flex;
              flex: 1;
              justify-content: flex-end;
            `}
          >
            <ApplicationAttackStatus
              small
              hasAProtectedServer={hasAProtectedServer}
              attackStatus={attackStatus}
            />
          </div>
        </div>
        <OpenVulnerabilitiesSeverityThermometer
          breakdown={vulnerabilityBreakdown}
        />
        <div
          className={css`
            display: flex;
            flex-direction: row;
            margin-top: 12px;
          `}
        >
          <FormattedMessage id={'title_url'}>
            {(urlMsg) => (
              <span
                className={cx(
                  smallMetadataCss,
                  css`
                    font-weight: ${deprecatedTheme.fontWeights.primary.bold};
                    margin-right: 4px;
                  `
                )}
              >
                {urlMsg}:
              </span>
            )}
          </FormattedMessage>

          <OverflowDetector>
            {({ doesOverflowParent, registerElement }) => {
              const AppUrl = () => (
                <span
                  ref={registerElement}
                  className={cx(
                    smallMetadataCss,
                    overflowCss,
                    css`
                      max-width: ${appURLMaxWidth}px;
                    `
                  )}
                  onMouseEnter={() => {
                    this.updateAppURLOverflowing(doesOverflowParent())
                  }}
                >
                  {url}
                </span>
              )

              return doesOverflowParent() ? (
                <WithTooltip
                  placement={'bottom'}
                  renderTooltipContent={() => (
                    <FormattedMessage id={'title_url'}>
                      {(urlMsg) => (
                        <React.Fragment>
                          <span
                            className={css`
                              font-weight: ${deprecatedTheme.fontWeights.primary
                                .bold};
                              margin-right: 4px;
                            `}
                          >
                            {urlMsg}:
                          </span>
                          {url}
                        </React.Fragment>
                      )}
                    </FormattedMessage>
                  )}
                  children={() => <AppUrl />}
                />
              ) : (
                <AppUrl />
              )
            }}
          </OverflowDetector>

          <FormattedMessage id={'action_applicationFlowMap_flowMapLink'}>
            {(linkMsg) => (
              <Link
                className={cx(
                  css`
                    font-size: 11px;
                    display: flex;
                    flex: 1;
                    justify-content: flex-end;
                  `
                )}
                href={getApplicationFlowMapRoute(organizationID, applicationID)}
              >
                {linkMsg}
              </Link>
            )}
          </FormattedMessage>
        </div>
      </div>
    )
  }

  private updateAppNameOverflowing = (overflowing: boolean) =>
    this.setState({ appNameOverflowing: overflowing })

  private updateAppURLOverflowing = (overflowing: boolean) =>
    this.setState({ appNameOverflowing: overflowing })
}
